import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  X, Brain, Target, Layers, Shield, Users, BarChart3, Map,
  Rocket, Lock, AlertTriangle, CheckCircle2, ArrowDown
} from 'lucide-react';

interface AgenticAIModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabId = 'esprit' | 'cadre' | 'autonomie' | 'raci' | 'architecture' | 'securite' | 'usecases' | 'roadmap' | 'kpi' | 'resume';

const tabs: { id: TabId; label: string; icon: typeof Brain }[] = [
  { id: 'esprit', label: 'Esprit & Objectifs', icon: Target },
  { id: 'cadre', label: 'Cadre Agentic', icon: Brain },
  { id: 'autonomie', label: 'Niveaux d\'autonomie', icon: Layers },
  { id: 'raci', label: 'RACI & Gouvernance', icon: Users },
  { id: 'architecture', label: 'Architecture', icon: Layers },
  { id: 'securite', label: 'Sécurité & Risques', icon: Shield },
  { id: 'usecases', label: 'Cas d\'usage BZ', icon: Rocket },
  { id: 'roadmap', label: 'Roadmap', icon: Map },
  { id: 'kpi', label: 'KPI & Mesure', icon: BarChart3 },
  { id: 'resume', label: 'Résumé exécutif', icon: CheckCircle2 },
];

function SectionCard({ title, icon: Icon, children }: { title: string; icon: React.ElementType; children: React.ReactNode }) {
  return (
    <div className="bg-white/5 rounded-xl p-6 border border-fuchsia-500/20">
      <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
        <Icon className="w-5 h-5 text-fuchsia-400" />
        {title}
      </h4>
      {children}
    </div>
  );
}

function Table({ headers, rows }: { headers: string[]; rows: (string | React.ReactNode)[][] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/10">
            {headers.map((h, i) => (
              <th key={i} className="text-left py-2 px-3 text-fuchsia-300 font-semibold">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-white/5 hover:bg-white/5">
              {row.map((cell, j) => (
                <td key={j} className="py-2 px-3 text-gray-300">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EspritTab() {
  return (
    <div className="space-y-6">
      <SectionCard title="Qu'est-ce que l'IA Agentique?" icon={Brain}>
        <div className="space-y-3 text-sm text-gray-300">
          <p>Un <strong className="text-white">agent IA</strong> est un système autonome qui :</p>
          <ul className="space-y-2 pl-4">
            <li>• <strong className="text-fuchsia-300">Perçoit</strong> son environnement (données, contexte, instructions)</li>
            <li>• <strong className="text-fuchsia-300">Planifie</strong> les actions nécessaires pour atteindre un objectif</li>
            <li>• <strong className="text-fuchsia-300">Exécute</strong> des actions via des outils (API, email, code, fichiers)</li>
            <li>• <strong className="text-fuchsia-300">S'ajuste</strong> selon les résultats — sous gouvernance humaine</li>
          </ul>
        </div>
      </SectionCard>

      <SectionCard title="Changement de modèle" icon={Target}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
            <p className="font-bold text-red-300 mb-2">Avant</p>
            <p className="text-gray-300">L'humain exécute. L'IA suggère.</p>
          </div>
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
            <p className="font-bold text-emerald-300 mb-2">Avec l'IA Agentique</p>
            <p className="text-gray-300"><strong className="text-white">Humain = gouverne.</strong> Agent = exécute.</p>
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Objectifs BZ" icon={Rocket}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-center">
          {[
            { label: 'Productivité', color: 'fuchsia' },
            { label: 'Standardisation', color: 'blue' },
            { label: 'Réduction coûts', color: 'emerald' },
            { label: 'Avantage concurrentiel', color: 'amber' },
          ].map(({ label, color }) => (
            <div key={label} className={`bg-${color}-500/10 border border-${color}-500/30 rounded-xl p-4`}>
              <p className={`font-bold text-${color}-300`}>{label}</p>
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard title="Positionnement PRIsME / TactiCE" icon={Shield}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="bg-fuchsia-500/10 border border-fuchsia-500/30 rounded-xl p-4">
            <p className="font-bold text-fuchsia-300 mb-2">PRIsME = Cadre de gouvernance</p>
            <p className="text-gray-300">Définit ce que les agents peuvent faire, qui les autorise, comment ils sont audités.</p>
          </div>
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
            <p className="font-bold text-blue-300 mb-2">TactiCE = Exécution</p>
            <p className="text-gray-300">Là où les agents opèrent, dans le respect du cadre PRIsME.</p>
          </div>
        </div>
        <div className="mt-4 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-sm">
          <p className="text-amber-200 font-semibold">⚠️ Message clé</p>
          <p className="text-gray-300 mt-1">"Un agent IA sans cadre de gouvernance n'est pas un outil — c'est un risque."</p>
        </div>
      </SectionCard>
    </div>
  );
}

function CadreTab() {
  return (
    <div className="space-y-6">
      <SectionCard title="Anatomie d'un agent IA" icon={Brain}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-center">
          {[
            { label: 'Objectif', desc: 'But à atteindre', color: 'fuchsia' },
            { label: 'Mémoire', desc: 'Court terme (session) + Long terme (RAG)', color: 'blue' },
            { label: 'Outils', desc: 'APIs, email, fichiers, code', color: 'emerald' },
            { label: 'Boucle', desc: 'Plan → Act → Validate → Adjust', color: 'amber' },
          ].map(({ label, desc, color }) => (
            <div key={label} className={`bg-${color}-500/10 border border-${color}-500/30 rounded-xl p-4`}>
              <p className={`font-bold text-${color}-300 mb-1`}>{label}</p>
              <p className="text-gray-400 text-xs">{desc}</p>
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard title="Boucle agentique" icon={Layers}>
        <div className="flex flex-wrap justify-center items-center gap-2 text-sm my-4">
          {['Plan', 'Act', 'Validate', 'Adjust'].map((step, i, arr) => (
            <div key={step} className="flex items-center gap-2">
              <div className="bg-fuchsia-500/20 border border-fuchsia-400/40 rounded-lg px-4 py-2 text-fuchsia-200 font-semibold">
                {step}
              </div>
              {i < arr.length - 1 && <span className="text-gray-500">→</span>}
            </div>
          ))}
          <div className="w-full text-center text-gray-500 text-xs mt-2">↑ Mémoire (contexte persistant) ↑</div>
        </div>
      </SectionCard>

      <SectionCard title="Concepts multi-agents" icon={Users}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <p className="font-bold text-white mb-2">🐝 Swarm (parallèle)</p>
            <p className="text-gray-300">Plusieurs agents travaillent en parallèle sur des sous-tâches indépendantes, puis consolident les résultats.</p>
          </div>
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <p className="font-bold text-white mb-2">🎯 Orchestration (hiérarchique)</p>
            <p className="text-gray-300">Un agent maître décompose l'objectif et délègue à des agents spécialisés en séquence ou selon le contexte.</p>
          </div>
        </div>
      </SectionCard>
    </div>
  );
}

function AutonomieLevelBadge({ level, color }: { level: string; color: string }) {
  return <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold bg-${color}-500/20 text-${color}-300`}>{level}</span>;
}

function AutonomieTab() {
  const levels = [
    { lvl: 'L0', name: 'Aucun agent', desc: 'Humain seul', risque: 'Nul', validation: '100%', color: 'gray' },
    { lvl: 'L1', name: 'Assistance', desc: 'Suggestions, aucune action autonome', risque: 'Très faible', validation: 'Toujours', color: 'green' },
    { lvl: 'L2', name: 'Automatisation guidée', desc: 'Exécute sur approbation explicite', risque: 'Faible', validation: 'Sur déclenchement', color: 'blue' },
    { lvl: 'L3', name: 'Autonomie partielle', desc: "Exécute, humain valide les résultats", risque: 'Modéré', validation: 'Post-exécution', color: 'yellow' },
    { lvl: 'L4', name: 'Autonomie supervisée', desc: "Exécute + escalade si incertitude", risque: 'Élevé', validation: 'Exception seulement', color: 'orange' },
    { lvl: 'L5', name: 'Autonomie complète', desc: 'Exécute sans validation', risque: 'Critique', validation: 'Rare / contexte strict', color: 'red' },
  ];

  return (
    <div className="space-y-6">
      <SectionCard title="Grille d'autonomie L0 → L5" icon={Layers}>
        <div className="space-y-3">
          {levels.map(({ lvl, name, desc, risque, validation, color }) => (
            <div key={lvl} className={`flex flex-col md:flex-row gap-2 md:items-center bg-${color}-500/10 border border-${color}-500/20 rounded-xl p-4`}>
              <div className={`text-${color}-300 font-bold text-lg w-10 shrink-0`}>{lvl}</div>
              <div className="flex-1">
                <p className="font-semibold text-white">{name}</p>
                <p className="text-gray-400 text-sm">{desc}</p>
              </div>
              <div className="flex gap-4 text-sm shrink-0">
                <div className="text-center">
                  <p className="text-gray-500 text-xs">Risque</p>
                  <p className={`text-${color}-300 font-medium`}>{risque}</p>
                </div>
                <div className="text-center">
                  <p className="text-gray-500 text-xs">Validation</p>
                  <p className="text-gray-300 font-medium">{validation}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 bg-fuchsia-500/10 border border-fuchsia-500/30 rounded-xl p-4 text-sm">
          <p className="text-fuchsia-200 font-semibold">🎯 Cible BZ</p>
          <p className="text-gray-300 mt-1">L2-L3 par défaut. L4-L5 exige validation du comité IA.</p>
        </div>
      </SectionCard>
    </div>
  );
}

function RACITab() {
  return (
    <div className="space-y-6">
      <SectionCard title="Matrice RACI" icon={Users}>
        <Table
          headers={['Activité', 'Direction', 'Ops / TI', 'Agent IA', 'Comité IA', 'Système audit']}
          rows={[
            ['Définir objectif agent', 'A', 'R', 'I', 'C', 'I'],
            ['Autoriser déploiement', 'A', 'C', 'I', 'R', 'I'],
            ['Exécuter tâche', 'I', 'C', 'R', 'I', 'I'],
            ['Valider résultat', 'I', 'R', 'I', 'A', 'I'],
            ['Auditer usage', 'C', 'I', 'I', 'A', 'R'],
            ['Modifier règles', 'A', 'C', 'I', 'R', 'I'],
          ]}
        />
        <p className="text-xs text-gray-500 mt-3">R = Responsable · A = Accountable · C = Consulté · I = Informé</p>
      </SectionCard>

      <SectionCard title="Alignement NIST AI RMF" icon={Shield}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          {[
            { fn: 'Govern', desc: 'PRIsME définit les règles, les rôles et les politiques de gouvernance des agents.', color: 'fuchsia' },
            { fn: 'Map', desc: 'Cartographier les risques par agent, par cas d\'usage et par niveau d\'autonomie.', color: 'blue' },
            { fn: 'Measure', desc: 'KPIs continus : erreurs, ROI, taux d\'autonomie, incidents sécurité.', color: 'emerald' },
            { fn: 'Manage', desc: 'Réponse aux incidents, escalade, désactivation d\'agent, audit post-mortem.', color: 'amber' },
          ].map(({ fn, desc, color }) => (
            <div key={fn} className={`bg-${color}-500/10 border border-${color}-500/30 rounded-xl p-4`}>
              <p className={`font-bold text-${color}-300 mb-1`}>{fn}</p>
              <p className="text-gray-300">{desc}</p>
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}

function ArchitectureTab() {
  const layers = [
    { label: 'DATA SENSIBLES', sub: 'SharePoint · OneDrive · CRM · Dossiers', color: 'red' },
    { label: 'RAG / Vector DB', sub: 'Base de connaissance privée, indexée et contrôlée', color: 'orange' },
    { label: 'ORCHESTRATEUR — OpenClaw', sub: 'Routing · Audit · Gouvernance · Journalisation', color: 'cyan', highlight: true },
    { label: 'LLM LOCAL — Qwen / Mistral', sub: '85% des usages · Confidentialité maximale', color: 'emerald' },
    { label: 'LLM EXTERNE — OpenRouter', sub: '15% · Cas complexes · Anonymisation obligatoire', color: 'violet' },
    { label: 'AGENTS SPÉCIALISÉS', sub: 'Chargé de projet · Support · Dev · Vente', color: 'fuchsia' },
    { label: 'OUTILS', sub: 'Email M365 · GitHub · PRIsME API · OneDrive', color: 'gray' },
  ];

  return (
    <div className="space-y-6">
      <SectionCard title="Architecture technique BZ" icon={Layers}>
        <div className="flex flex-col items-center gap-1 text-sm font-mono">
          {layers.map(({ label, sub, color, highlight }, i) => (
            <div key={label} className="w-full flex flex-col items-center">
              <div
                className={`w-full md:w-3/4 rounded-xl p-3 text-center border ${highlight ? 'border-2 border-cyan-400/80 shadow-lg shadow-cyan-500/20' : `border-${color}-500/40`} bg-${color}-500/10`}
              >
                <p className={`font-bold text-${color}-300`}>{label}</p>
                <p className="text-gray-400 text-xs mt-0.5">{sub}</p>
              </div>
              {i < layers.length - 1 && (
                <ArrowDown className="w-4 h-4 text-gray-600 my-0.5" />
              )}
            </div>
          ))}
        </div>
        <div className="mt-4 bg-white/5 rounded-xl p-4 text-sm border border-white/10">
          <p className="font-semibold text-white mb-2">Règle de routing</p>
          <p className="text-gray-300">Local first → externe uniquement si nécessaire, après anonymisation obligatoire.</p>
        </div>
      </SectionCard>
    </div>
  );
}

function SecuriteTab() {
  return (
    <div className="space-y-6">
      <SectionCard title="Principes de sécurité" icon={Lock}>
        <ul className="space-y-2 text-sm text-gray-300">
          {[
            'Deny by default — whitelist des actions permises par agent',
            'Anonymisation obligatoire avant tout LLM externe',
            'Audit logs immuables — toutes les actions tracées',
            'Accès granulaire par agent (permissions minimales)',
            'Chiffrement des données sensibles (vault)',
            'Validation humaine obligatoire avant actions irréversibles',
          ].map((p) => (
            <li key={p} className="flex items-start gap-2">
              <Shield className="w-4 h-4 text-fuchsia-400 shrink-0 mt-0.5" />
              {p}
            </li>
          ))}
        </ul>
      </SectionCard>

      <SectionCard title="Matrice de risques" icon={AlertTriangle}>
        <Table
          headers={['Risque', 'Probabilité', 'Impact', 'Mitigation']}
          rows={[
            ['Hallucination', 'Élevée', 'Modéré', 'RAG + validation humaine'],
            ['Fuite de données', 'Faible', <span className="text-red-400 font-semibold">Critique</span>, 'Anonymisation + LLM local'],
            ['Mauvaise décision', 'Modérée', 'Élevé', 'L2-L3 par défaut + audit'],
            ['Dépendance externe', 'Modérée', 'Modéré', 'Local first architecture'],
            ['Prompt injection', 'Faible', 'Élevé', 'Sandboxing + filtrage'],
          ]}
        />
      </SectionCard>
    </div>
  );
}

function UseCasesTab() {
  const cases = [
    {
      title: 'Agent Chargé de Projet',
      desc: 'Suivi mandats, alertes échéances, rapports automatiques',
      level: 'L3',
      outils: 'PRIsME, calendrier, email M365',
      roi: '8h/semaine économisées par PM',
      color: 'fuchsia',
    },
    {
      title: 'Agent Support Client',
      desc: 'Réponses automatiques, escalade intelligente, historique',
      level: 'L2',
      outils: 'CRM, email, base de connaissance',
      roi: '60% réduction temps de réponse',
      color: 'blue',
    },
    {
      title: 'Agent Vente / Suivi',
      desc: 'Suivi pipeline, relances automatiques, rapports hebdo',
      level: 'L2',
      outils: 'CRM, email, Google Sheets',
      roi: '5h/semaine par vendeur',
      color: 'emerald',
    },
    {
      title: 'Agent Dev / Backlog',
      desc: 'Triage tickets, suggestions priorités, documentation auto',
      level: 'L3',
      outils: 'GitHub, Jira, Confluence',
      roi: '30% réduction temps de triage',
      color: 'amber',
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {cases.map(({ title, desc, level, outils, roi, color }) => (
          <div key={title} className={`bg-${color}-500/10 border border-${color}-500/30 rounded-xl p-5`}>
            <p className={`font-bold text-${color}-300 text-base mb-1`}>{title}</p>
            <p className="text-gray-300 text-sm mb-3">{desc}</p>
            <div className="space-y-1 text-xs text-gray-400">
              <p><span className="text-gray-500">Autonomie :</span> <span className={`text-${color}-300 font-semibold`}>{level}</span></p>
              <p><span className="text-gray-500">Outils :</span> {outils}</p>
              <p><span className="text-gray-500">ROI estimé :</span> <span className="text-green-400 font-semibold">{roi}</span></p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RoadmapTab() {
  const phases = [
    {
      phase: 'Phase 1', period: '0–30 jours', title: 'Structuration',
      items: ['Cartographier les processus cibles', 'Définir RACI et règles dans PRIsME', 'Identifier 2-3 premiers cas d\'usage', 'Installer infra de base (OpenClaw, RAG)'],
      color: 'fuchsia',
    },
    {
      phase: 'Phase 2', period: '1–2 mois', title: 'Agents simples',
      items: ['Déployer 1 agent mono-outil', 'Valider la boucle plan→act→validate', 'Mesurer les premiers KPIs'],
      color: 'blue',
    },
    {
      phase: 'Phase 3', period: '2–3 mois', title: 'Agents multi-tools',
      items: ['Agents avec 3-5 outils connectés', 'RAG opérationnel avec données BZ', 'Reporting hebdomadaire automatique'],
      color: 'emerald',
    },
    {
      phase: 'Phase 4', period: '3–6 mois', title: 'Orchestration',
      items: ['Multi-agents coordonnés', 'Routing intelligent local/externe', 'Tableau de bord gouvernance temps réel'],
      color: 'amber',
    },
    {
      phase: 'Phase 5', period: '6–12 mois', title: 'Optimisation',
      items: ['Réduction des coûts LLM (local first max)', 'Affinement des modèles sur données BZ', 'Expansion vers nouveaux cas d\'usage'],
      color: 'violet',
    },
  ];

  return (
    <div className="space-y-4">
      {phases.map(({ phase, period, title, items, color }) => (
        <div key={phase} className={`bg-${color}-500/10 border border-${color}-500/20 rounded-xl p-5`}>
          <div className="flex items-center gap-3 mb-3">
            <span className={`text-${color}-300 font-bold`}>{phase}</span>
            <span className="text-gray-500 text-sm">{period}</span>
            <span className="text-white font-semibold">— {title}</span>
          </div>
          <ul className="space-y-1 text-sm text-gray-300">
            {items.map((item) => (
              <li key={item} className="flex items-start gap-2">
                <CheckCircle2 className={`w-4 h-4 text-${color}-400 shrink-0 mt-0.5`} />
                {item}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function KPITab() {
  return (
    <div className="space-y-6">
      <SectionCard title="Indicateurs de performance" icon={BarChart3}>
        <Table
          headers={['KPI', 'Définition', 'Cible', 'Fréquence']}
          rows={[
            ['Gain de temps', 'Heures économisées / agent / sem', '>5h', 'Hebdo'],
            ['Réduction coûts', 'Coût évité vs processus manuel', '>30%', 'Mensuel'],
            ['Taux d\'erreur', '% actions nécessitant correction', '<5%', 'Hebdo'],
            ['Taux d\'autonomie', '% tâches sans intervention humaine', '60–80%', 'Mensuel'],
            ['ROI par agent', '(valeur créée - coût) / coût', '>300% an 1', 'Trimestriel'],
            ['Incidents sécurité', 'Nb d\'écarts de sécurité liés à l\'IA', '0', 'Continu'],
          ]}
        />
      </SectionCard>
    </div>
  );
}

function ResumeTab() {
  return (
    <div className="space-y-6">
      <SectionCard title="Pourquoi maintenant" icon={Rocket}>
        <ul className="space-y-2 text-sm text-gray-300">
          <li>• La fenêtre d'avantage concurrentiel IA est de <strong className="text-white">12–24 mois</strong></li>
          <li>• Les coûts LLM baissent de <strong className="text-white">80% par an</strong></li>
          <li>• Les frameworks d'orchestration (OpenClaw) sont <strong className="text-white">matures</strong></li>
          <li>• BZ a déjà l'infrastructure en place : OpenClaw, PRIsME, RAG</li>
        </ul>
      </SectionCard>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SectionCard title="Risques" icon={AlertTriangle}>
          <ul className="space-y-2 text-sm text-gray-300">
            <li>• Déploiement sans cadre → agents incontrôlables</li>
            <li>• Dépendance aux LLM externes → coûts et confidentialité</li>
            <li>• Résistance interne → nécessite formation et adoption progressive</li>
          </ul>
        </SectionCard>

        <SectionCard title="Opportunités" icon={Target}>
          <ul className="space-y-2 text-sm text-gray-300">
            <li>• Réduction de 30–50% du temps opérationnel sur tâches répétitives</li>
            <li>• Standardisation des processus via PRIsME</li>
            <li>• Positionnement BZ comme leader IA auprès des clients</li>
          </ul>
        </SectionCard>
      </div>

      <SectionCard title="Recommandation" icon={CheckCircle2}>
        <ul className="space-y-2 text-sm text-gray-300">
          <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />Lancer Phase 1 immédiatement (structuration + 2 agents pilotes)</li>
          <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />Comité IA mensuel obligatoire (gouvernance PRIsME)</li>
          <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />Objectif 12 mois : 5 agents en production, ROI mesuré et documenté</li>
          <li className="flex items-start gap-2"><AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />Ne pas déployer en L4-L5 sans validation du comité IA</li>
        </ul>
      </SectionCard>
    </div>
  );
}

export default function AgenticAIModal({ isOpen, onClose }: AgenticAIModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('esprit');

  const renderTab = () => {
    switch (activeTab) {
      case 'esprit': return <EspritTab />;
      case 'cadre': return <CadreTab />;
      case 'autonomie': return <AutonomieTab />;
      case 'raci': return <RACITab />;
      case 'architecture': return <ArchitectureTab />;
      case 'securite': return <SecuriteTab />;
      case 'usecases': return <UseCasesTab />;
      case 'roadmap': return <RoadmapTab />;
      case 'kpi': return <KPITab />;
      case 'resume': return <ResumeTab />;
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: 8 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-7xl max-h-[92vh] overflow-y-auto rounded-2xl border border-fuchsia-500/20 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 shadow-2xl"
          >
            {/* Header */}
            <div className="sticky top-0 z-20 bg-slate-950/90 backdrop-blur border-b border-fuchsia-500/20 px-6 py-4 flex items-start justify-between gap-4">
              <div>
                <p className="uppercase tracking-[0.25em] text-fuchsia-300 text-xs mb-1">BZ — Gouvernance</p>
                <h2 className="text-3xl font-bold text-white">IA Agentique</h2>
                <p className="text-gray-400 mt-1 text-sm max-w-3xl">Cadre BZ pour comprendre, encadrer et déployer des agents IA en production.</p>
              </div>
              <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-gray-300 hover:text-white transition-colors">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Tabs */}
            <div className="px-6 pt-4 pb-2 border-b border-white/10 flex flex-wrap gap-1">
              {tabs.map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    activeTab === id ? 'bg-fuchsia-600 text-white' : 'text-gray-400 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="p-6">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.15 }}
                >
                  {renderTab()}
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
